import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { CuadroDeBusqueda } from '../Buscable';
import { filtrarFilas } from '../busqueda';

import { almacen, type Supplier } from '../api';
import {
  Cabecera,
  Cargando,
  Fallo,
  Pastilla,
  Tarjeta,
  Vacio,
} from '../componentes';
import { contados, dia, iniciales, soles } from '../formato';
import { FichaDeProveedor, Retirados, Retirar } from '../formularios';
import { mayuscula, useVocabulario } from '../vocabulario';

const ESTADO: Record<Supplier['status'], { texto: string; tono: 'exito' | 'neutro' | 'aviso' }> = {
  preferred: { texto: 'Preferido', tono: 'exito' },
  active: { texto: 'Activo', tono: 'neutro' },
  observed: { texto: 'Observado', tono: 'aviso' },
};

/**
 * A quién se le compra, y en qué condiciones.
 *
 * El estado lo pone una persona, no se calcula. Un proveedor que falla la mitad
 * de las entregas puede seguir siendo el único que trae ese queso, y un número
 * no sabe eso.
 */
export function Proveedores() {
  const vocabulario = useVocabulario();
  const consulta = useQuery({
    queryKey: ['proveedores'],
    queryFn: almacen.suppliers,
  });

  const [texto, setTexto] = useState('');
  const todos = consulta.data ?? [];
  const proveedores = filtrarFilas(todos, texto, (proveedor) => [
    proveedor.name,
    proveedor.taxId,
    proveedor.suppliesSummary,
    proveedor.contactName,
    proveedor.phone,
  ]);

  return (
    <>
      <Cabecera
        titulo="Proveedores"
        subtitulo="Condiciones comerciales y qué se le compra a cada uno" ayuda="A quién se le compra, cómo se le paga y cuánto tarda. Con el teléfono, los pedidos se mandan por WhatsApp."
        acciones={
          <>
            <Retirados que="proveedores" />
            <FichaDeProveedor />
          </>
        }
      />

      <div className="cuerpo">
        {consulta.isError ? (
          <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} />
        ) : null}

        {todos.length > 0 ? (
          <div className="filtros">
            <CuadroDeBusqueda
              valor={texto}
              onCambio={setTexto}
              placeholder="Buscar proveedor, RUC o lo que vende…"
              etiquetaAccesible="Buscar proveedor"
            />
            <span className="recuento">
              {proveedores.length} DE {todos.length} PROVEEDORES
            </span>
          </div>
        ) : null}

        {consulta.isLoading ? (
          <Cargando que="los proveedores" />
        ) : todos.length === 0 ? (
          <Vacio
            titulo="Ningún proveedor dado de alta"
            mensaje={`Cada ${vocabulario.insumo} puede apuntar a quién se le pide. Sin eso, la reposición sugerida es una lista de cantidades que nadie sabe a dónde mandar.`}
            accion={<FichaDeProveedor />}
          />
        ) : (
          <div className="rejilla-tarjetas">
            {proveedores.map((proveedor) => (
              <Tarjeta key={proveedor.id}>
                <div className="proveedor__cabeza">
                  <span className="proveedor__inicial" aria-hidden="true">
                    {iniciales(proveedor.name)}
                  </span>
                  <div className="tarjeta__nombre-caja">
                    <strong className="tarjeta__nombre">
                      {proveedor.name}
                    </strong>
                    <p className="mono tenue tarjeta__sub">
                      {/*
                       * Media compra de un restaurante peruano es el mercado de
                       * la esquina, que no da RUC. Se dice, en vez de dejar el
                       * hueco: un vacío hace pensar que falta el dato.
                       */}
                      {proveedor.taxId === null
                        ? 'Sin RUC'
                        : `RUC ${proveedor.taxId}`}
                    </p>
                  </div>
                  <Pastilla {...ESTADO[proveedor.status]} />
                </div>

                {proveedor.suppliesSummary === null ? null : (
                  <p className="nota">
                    {proveedor.suppliesSummary}
                  </p>
                )}

                <dl className="resumen-pares">
                  <div>
                    <dt>Tiempo de entrega</dt>
                    <dd>
                      {contados(proveedor.leadTimeDays, 'día', 'días')}
                    </dd>
                  </div>
                  <div>
                    <dt>Condición de pago</dt>
                    <dd>{proveedor.paymentTerms ?? 'Sin acordar'}</dd>
                  </div>
                  <div>
                    <dt>Pedido mínimo</dt>
                    <dd>
                      {proveedor.minimumOrder === null
                        ? 'Sin mínimo'
                        : soles(proveedor.minimumOrder)}
                    </dd>
                  </div>
                  <div>
                    <dt>{mayuscula(vocabulario.insumos)} que trae</dt>
                    <dd className="mono">{proveedor.supplyCount}</dd>
                  </div>
                  <div>
                    <dt>Última recepción</dt>
                    <dd>
                      {proveedor.lastReceivedAt === null
                        ? 'Nunca'
                        : dia(proveedor.lastReceivedAt)}
                    </dd>
                  </div>
                </dl>

                <div className="formulario__fila">
                  <FichaDeProveedor proveedor={proveedor} />
                  <Retirar
                    nombre={proveedor.name}
                    retirar={() => almacen.retireSupplier(proveedor.id)}
                  />
                </div>
              </Tarjeta>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
